import type { FC } from "react";
import { shallowEqual, useSelector } from "react-redux";
import { Navigate } from "react-router";
import { CardGridArea, ColoredLabel, formatDateTimeFromISO } from "widgets";

import { getEventTagIntent, getTagIntentColor } from "@/app-utils/events.js";
import { getIconElementForPlaybook } from "@/app-utils/playbook.js";
import { getPlaybookEventName, type IEventState } from "@/redux/modules/event.js";
import type { IAppState } from "@/redux/reducer.js";
import { PlaybookEventErrorsCard } from "@/ui-modules/playbooks/events/PlaybookEventErrorsCard.tsx";
import { PlaybookEventInputsCard } from "@/ui-modules/playbooks/events/PlaybookEventInputCard.tsx";
import { PlaybookEventOutputCard } from "@/ui-modules/playbooks/events/PlaybookEventOutputCard.tsx";
import { PlaybookEventSettingsCard } from "@/ui-modules/playbooks/events/PlaybookEventSettingsCard.tsx";

import { AttributesListWidget } from "../core/navigation-panel/AttributeWidget.js";
import GenericEntitySummaryView from "./GenericEntitySummaryView.js";

const EventSummaryView: FC = () => {
  const { event, isLoading } = useSelector<IAppState, IEventState>((state) => state.event, shallowEqual);

  // if this is a job-related event, proceed to the job summary page right away
  if (event?.job) {
    return <Navigate to={`/job/${event?.job?.id}`} replace />;
  }

  return (
    <GenericEntitySummaryView
      title={getPlaybookEventName(event)}
      subtitle={"Event Summary"}
      entity={event}
      project={event?.project}
      headerTags={
        event
          ? [
              <img
                src={getIconElementForPlaybook(event.playbook).large}
                width={53}
                height={30}
                key={"event-summary-view-tag-3"}
              />,
            ]
          : undefined
      }
      headerAttributes={
        event
          ? [
              <AttributesListWidget
                attributes={[
                  { label: "Date", description: formatDateTimeFromISO(event.created) },
                  { label: "Playbook", description: event?.playbook.name },
                  {
                    label: "Status",
                    description: (
                      <ColoredLabel color={getTagIntentColor(getEventTagIntent(event.status))} label={event.status} />
                    ),
                  },
                ]}
                key={"event-summary-view-widget-2"}
              />,
            ]
          : undefined
      }
      isLoading={isLoading}
    >
      {event && (
        <>
          <PlaybookEventSettingsCard eventId={event.id} area={CardGridArea.Left} />
          <PlaybookEventInputsCard eventId={event.id} area={CardGridArea.Left} />
          <PlaybookEventErrorsCard eventId={event.id} area={CardGridArea.Right} />
          <PlaybookEventOutputCard eventId={event.id} area={CardGridArea.Right} />
        </>
      )}
    </GenericEntitySummaryView>
  );
};

export default EventSummaryView;
